/**
 * BabyCareTodayDetail
 *
 * Shows today's activity breakdown for the main baby care page.
 */

import { useMemo } from 'react';
import { Baby, Moon, Pill, Thermometer, StickyNote, Droplets } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { BabyCareEntry, CareType } from '../index';


const CARE_LABEL_KEYS: Record<CareType, string> = {
  feeding: 'stats.categories.feedings',
  diaper: 'stats.categories.diapers',
  sleep: 'stats.categories.sleepPhases',
  medication: 'stats.categories.medications',
  temperature: 'stats.categories.measurements',
  notes: 'stats.categories.notes',
};

function getIconComponent(type: CareType) {
  const icons = {
    feeding: Baby,
    diaper: Droplets,
    sleep: Moon,
    medication: Pill,
    temperature: Thermometer,
    notes: StickyNote,
  };
  return icons[type];
}

function formatDuration(minutes: number, tFn: (key: string) => string): string {
  if (minutes < 60) return `${minutes} ${tFn('stats.minuteShort')}`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}${tFn('stats.minuteLong')}`;
}

interface BabyCareTodayDetailProps {
  entries: BabyCareEntry[];
}

interface DailySummary {
  care_type: CareType;
  entry_count: number;
  total_duration_minutes: number;
  first_entry?: string;
  last_entry?: string;
}

export function BabyCareTodayDetail({ entries }: BabyCareTodayDetailProps) {
  const { t } = useTranslation('babyCare');

  // Filter to today's entries only
  const todayEntries = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return entries.filter(e => new Date(e.recorded_at) >= today);
  }, [entries]);

  // Calculate summary by care type
  const dailySummary = useMemo((): DailySummary[] => {
    const summaryMap = new Map<CareType, DailySummary>();

    todayEntries.forEach(entry => {
      const existing = summaryMap.get(entry.care_type);

      // Calculate duration for sleep entries
      let duration = 0;
      if (entry.care_type === 'sleep' && entry.ended_at) {
        const start = new Date(entry.recorded_at).getTime();
        const end = new Date(entry.ended_at).getTime();
        duration = Math.floor((end - start) / (1000 * 60));
      }

      if (existing) {
        existing.entry_count++;
        existing.total_duration_minutes += duration;
        if (!existing.first_entry || entry.recorded_at < existing.first_entry) {
          existing.first_entry = entry.recorded_at;
        }
        if (!existing.last_entry || entry.recorded_at > existing.last_entry) {
          existing.last_entry = entry.recorded_at;
        }
      } else {
        summaryMap.set(entry.care_type, {
          care_type: entry.care_type,
          entry_count: 1,
          total_duration_minutes: duration,
          first_entry: entry.recorded_at,
          last_entry: entry.recorded_at,
        });
      }
    });

    return Array.from(summaryMap.values());
  }, [todayEntries]);

  if (dailySummary.length === 0) {
    return null;
  }

  // Build value string for each summary row
  const formatValue = (summary: DailySummary): string => {
    if (summary.total_duration_minutes > 0) {
      return `${summary.entry_count}x · ${formatDuration(summary.total_duration_minutes, t)}`;
    }
    return `${summary.entry_count}x`;
  };

  return (
    <div>
      <h3 className="text-white font-medium mb-3">{t('todayDetail.title')}</h3>
      <div>
        {dailySummary.map((summary, i) => {
          const IconComponent = getIconComponent(summary.care_type);

          return (
            <div key={summary.care_type}>
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                    <IconComponent className="w-4 h-4 text-white/50" />
                  </div>
                  <span className="text-white/70 text-sm">{t(CARE_LABEL_KEYS[summary.care_type])}</span>
                </div>
                <span className="text-white text-2xl font-bold">{formatValue(summary)}</span>
              </div>
              {i < dailySummary.length - 1 && (
                <div className="h-px bg-white/25" />
              )}
            </div>
          );
        })}
      </div>

      {/* Time Range */}
      {dailySummary.some(s => s.first_entry) && (
        <div className="mt-2 pt-3 border-t border-white/10">
          <div className="flex justify-between text-sm text-white/50">
            <span>{t('todayDetail.firstActivity')}</span>
            <span>
              {dailySummary
                .filter(s => s.first_entry)
                .map(s => new Date(s.first_entry!).getTime())
                .sort((a, b) => a - b)[0]
                ? new Date(
                    dailySummary
                      .filter(s => s.first_entry)
                      .map(s => new Date(s.first_entry!).getTime())
                      .sort((a, b) => a - b)[0]
                  ).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                : '-'}
            </span>
          </div>
          <div className="flex justify-between text-sm text-white/50 mt-1">
            <span>{t('todayDetail.lastActivity')}</span>
            <span>
              {dailySummary
                .filter(s => s.last_entry)
                .map(s => new Date(s.last_entry!).getTime())
                .sort((a, b) => b - a)[0]
                ? new Date(
                    dailySummary
                      .filter(s => s.last_entry)
                      .map(s => new Date(s.last_entry!).getTime())
                      .sort((a, b) => b - a)[0]
                  ).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                : '-'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
